import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Building2, Copy, Check, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { getHrPitchFn, submitHrReferralFn } from "@/lib/employerBenefits.functions";

interface Props {
  employerName: string;
  firstName?: string | null;
  className?: string;
}

/**
 * "Ask your HR" — turns a member into a warm employer intro.
 * Two paths: copy a prewritten note and send it themselves, or hand us the
 * HR contact and we send a co-branded intro that credits them.
 */
export default function EmployerReferralFlow({ employerName, firstName, className }: Props) {
  const getPitch = useServerFn(getHrPitchFn);
  const submit = useServerFn(submitHrReferralFn);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"self" | "platform" | null>(null);
  const [pitch, setPitch] = useState<{ subject: string; body: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [nameMember, setNameMember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function start(next: "self" | "platform") {
    setMode(next);
    setOpen(true);
    if (!pitch) {
      try {
        const res = await getPitch({ data: { employerName, firstName: firstName ?? null } });
        setPitch({ subject: res.subject, body: res.body });
      } catch {
        toast.error("Couldn't load the note. Try again in a moment.");
      }
    }
  }

  async function copyNote() {
    if (!pitch) return;
    await navigator.clipboard.writeText(`Subject: ${pitch.subject}\n\n${pitch.body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    try {
      await submit({
        data: { employerName, sendMethod: "self", firstName: firstName ?? null, nameMember: true },
      });
      setDone(true);
    } catch {
      /* copying still worked — don't nag */
    }
  }

  async function sendForMe() {
    if (!contactEmail.trim()) {
      toast.error("Add your HR contact's email first.");
      return;
    }
    setBusy(true);
    try {
      const res = await submit({
        data: {
          employerName,
          sendMethod: "platform",
          nameMember,
          firstName: firstName ?? null,
          contactName: contactName.trim() || null,
          contactEmail: contactEmail.trim(),
        },
      });
      setDone(true);
      toast.success(
        (res as any)?.duplicate
          ? "You've already referred this employer — thank you!"
          : "Sent. We'll let you know if they respond.",
      );
    } catch {
      toast.error("Couldn't send that. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  const mailto = pitch
    ? `mailto:?subject=${encodeURIComponent(pitch.subject)}&body=${encodeURIComponent(pitch.body)}`
    : "#";

  return (
    <section className={`rounded-xl border bg-card p-5 ${className ?? ""}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold">
            Ask {employerName} to add a homeownership benefit
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Employers can add down payment help on top of the programs you already qualify for.
            We wrote the note for you — send it yourself, or we'll send it for you.
          </p>

          {done ? (
            <p className="mt-4 flex items-center gap-2 text-sm font-medium text-primary">
              <Check className="h-4 w-4" /> Thanks — your referral is logged. You earned 100 points.
            </p>
          ) : !open ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => start("self")}>
                <Copy className="mr-2 h-4 w-4" /> Send it myself
              </Button>
              <Button size="sm" variant="outline" onClick={() => start("platform")}>
                <Send className="mr-2 h-4 w-4" /> We'll send it
              </Button>
            </div>
          ) : mode === "self" ? (
            <div className="mt-4 space-y-3">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Your note to HR
              </Label>
              <Textarea
                readOnly
                rows={10}
                className="text-sm"
                value={pitch ? `Subject: ${pitch.subject}\n\n${pitch.body}` : "Loading…"}
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={copyNote} disabled={!pitch}>
                  {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                  {copied ? "Copied" : "Copy note"}
                </Button>
                <Button size="sm" variant="outline" asChild disabled={!pitch}>
                  <a href={mailto}>
                    <Mail className="mr-2 h-4 w-4" /> Open in email
                  </a>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMode("platform")}>
                  We'll send it instead
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="hr-name">HR contact name</Label>
                  <Input
                    id="hr-name"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hr-email">HR contact email</Label>
                  <Input
                    id="hr-email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="hr@company.com"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={nameMember}
                  onCheckedChange={(v) => setNameMember(v === true)}
                />
                Mention my first name in the intro
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={sendForMe} disabled={busy}>
                  <Send className="mr-2 h-4 w-4" /> {busy ? "Sending…" : "Send intro"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMode("self")}>
                  I'll send it myself
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
